import { useState } from 'react';
import { Plus } from 'lucide-react';
import { QueryState, RecordTable, StatusPill, type Column } from '@/components/live';
import { ValidityBadge } from '@/components/ValidityBadge';
import { NameVerificationPanel, documentOf, type NameVerificationRow } from '@/components/NameVerificationPanel';
import { DeleteRecordButton } from '@/components/DeleteRecordButton';
import { useRows } from '@/hooks/useResource';
import { useAuth } from '@/auth/AuthProvider';
import { can } from '@/auth/session';
import { KycRecordDrawer } from '@/screens/parts/KycRecordDrawer';
import type { KycRow } from '@/types/records';

/**
 * A farmer's KYC: one record per document they bring, the name checks those documents raised, and
 * the way to add another. Shown on the farmer's KYC tab and in the farmer form's own KYC tab (owner,
 * 2026-09-27: "farmer KYC can be added to farmer register as a tab? after member tab?").
 */
export function FarmerKycSection({ partyId, partyLabel, inline = false }: {
  partyId: string; partyLabel: string;
  /** The add form drawn in place, always open (the farmer form's KYC tab), not behind a button. */
  inline?: boolean;
}) {
  const { profile } = useAuth();
  const [adding, setAdding] = useState(false);
  const mayAdd = can(profile, 'party.kyc', 'create');
  const kyc = useRows<KycRow>('party_kyc_records', { filters: { party_id: partyId } });
  const nameChecks = useRows<NameVerificationRow>('name_verifications', {
    filters: { party_id: partyId, conflict_type: 'kyc_document_vs_id' }, orderBy: { column: 'created_at', ascending: false },
  });

  const columns: Array<Column<KycRow>> = [
    { key: 'kyc_type_label', label: 'Record type', render: (row) => row.kyc_type_label ?? '--' },
    { key: 'masked_document_no', label: 'Number', render: (row) => row.masked_document_no ?? '--' },
    { key: 'document_id', label: 'File', render: (row) => (row.document_id ? 'Attached' : <span className="muted">None</span>) },
    { key: 'valid_to', label: 'Validity', render: (row) => (row.valid_to ? <ValidityBadge from={row.valid_from} to={row.valid_to} /> : <span className="muted">No expiry</span>) },
    { key: 'effective_status', label: 'Status', render: (row) => <StatusPill value={row.effective_status ?? row.verification_status} /> },
    {
      key: 'delete', label: '',
      render: (row) => (
        <DeleteRecordButton entity="kyc" id={row.id} invalidate={['party_kyc_records', 'farmers']}
                            label={`${row.kyc_type_label ?? 'KYC record'} ${row.masked_document_no ?? ''}`.trim()}
                            onDeleted={() => undefined} screenKey="party.kyc" />
      ),
    },
  ];

  return (
    <>
      {inline ? (
        mayAdd && <KycRecordDrawer inline onClose={() => undefined} open partyId={partyId} partyLabel={partyLabel} />
      ) : (
        <div className="toolbar-row">
          <span className="muted">One record per document the farmer brings. Each needs verifying by someone else.</span>
          <button className="primary" disabled={!mayAdd} onClick={() => setAdding(true)}
                  title={mayAdd ? undefined : 'Your role cannot record KYC.'} type="button">
            <Plus size={16} /> Add KYC record
          </button>
        </div>
      )}
      <QueryState empty="No KYC evidence recorded for this farmer yet." query={kyc}>
        <RecordTable columns={columns} rows={kyc.data ?? []} />
      </QueryState>
      {/* A KYC document printing the name differently from the ID card is checked beside it. */}
      {(nameChecks.data ?? []).length > 0 && (
        <>
          <div className="section-heading"><h3>Name checks</h3></div>
          {(nameChecks.data ?? []).map((row) => (
            <section className="name-check" key={row.id}>
              <strong>{row.verification_code ?? 'Name verification'} <span className="muted">· {documentOf(row)}</span></strong>
              <NameVerificationPanel row={row} />
            </section>
          ))}
        </>
      )}
      {!inline && <KycRecordDrawer onClose={() => setAdding(false)} open={adding} partyId={partyId} partyLabel={partyLabel} />}
    </>
  );
}
